<template>
    <div class="page-content" v-if="ready">
        <h1>
            Simulator
        </h1>
        <div class="header">
            <p>The Simulator tool allows users to model the outcomes of applying different pre-filters via Marketplace, to understand the impact on expected volumes coming to their APIs.</p>
        </div>
        <br />
        <div class="form-content">
            <div class="column">
                <Form @submit="onSubmit" v-slot="{ errors,meta,values,setFieldValue,validate }">
                    <div class="top">
                        <div class="vertical">
                            <p style="margin-bottom:0">Vertical:</p>
                            <Field name="Vertical" v-model="selectedVertical" as="select" :rules="`${'required'}`" :class="{'dropdown': true, 'invalid': errors.Vertical}" @change="refreshForm(); getVerticalFilterList()">
                                <option disabled value="">Please select one</option>
                                <option v-for="name in verticals" :key="name.value" :value="name.value">{{name.value}}</option>
                            </Field>
                            <p style="display:flex" class="invalidText">{{ errors.Vertical }}</p>
                        </div>
                        <div class="aggregator" v-if="aggregatorList && aggregatorList.length > 1">
                            <p style="margin-bottom:0">Aggregators:</p>
                            <Field name="Aggregator" v-model="selectedAggregators" as="select" :rules="`${'required'}`" id="verticalDropdown" :class="{'multiselect': true, 'dropdown': true, 'invalid': errors.Aggregator}" style="height: auto;" v-slot="{ value }" size="5" multiple>
                                <option disabled value="">Please select one or more</option>
                                <option v-for="aggregator in filterAggregatorList(aggregatorList)" :key="aggregator.aggregatorName" :value="aggregator.aggregatorName" :selected="value && value.includes(aggregator.aggregatorName)">{{aggregator.aggregatorName}}</option>
                            </Field>
                            <p style="display:flex" class="invalidText">{{ errors.Aggregator }}</p>
                        </div>

                    </div>
                    <p class="invalidText">{{ loadFilterError }}</p>
                    <template v-if="selectedVertical && aggregatorList.length > 1">
                        <p style="margin-bottom:0">Filters (to exclude people defined by these rules):</p>
                        <div v-for="(filter, index) in filters" :key="index" class="filterInputContainer">
                            <div class="filterInput">
                                <Field :name="`Filter${index}`" :value="filter.name" v-slot="{ field }" label="Filter type" :rules="`${'required'}`">
                                    <v-select v-bind="field"
                                              v-model="filter.name"
                                              :options="filterNames"
                                              :reduce="(passedFilter) => passedFilter.name"
                                              :class="{'searchDropdown': true, 'invalid': errors[`Filter${index}`]}"
                                              :id="`filter${index}`"
                                              @update:modelValue="resetFilter(index)">
                                    </v-select>
                                </Field>

                                <!-- Start Numeric operator-->
                                <Field v-if="isArithmeticFilter(filter.name, filter.operator)" :name="`ArithmeticOperator${index}`" label="Arithmetic Operator" as="select" @change="getArithmeticFilterList(filter.arithmeticOperator)" v-model="filter.arithmeticOperator" :class="{'dropdown':true, 'operator':true}">
                                    <option value="">No Operator</option>
                                    <option v-for="operator in arithmeticOperators" :key="operator.value" :value="operator.name">{{operator.value}}</option>
                                </Field>
                                <Field v-if="isArithmeticFilter(filter.name, filter.operator) && filter.arithmeticOperator !=''" :name="`Filter2-${index}`" :value="filter.name2" v-slot="{ field }" label="Filter type" :rules="`${'required'}`">
                                    <v-select v-bind="field"
                                              v-model="filter.name2"
                                              :options="filterNames2"
                                              :reduce="(passedFilter) => passedFilter.name"
                                              :class="{'searchDropdown': true,'invalid': errors[`Filter2-${index}`]}"
                                              :id="`filter2-${index}`">
                                    </v-select>
                                </Field>

                                <Field v-if="isOperatorFilter(filter.name)" :name="`Operator${index}`" label="Operator" as="select" :rules="`${'required'}`" v-model="filter.operator" :class="{'dropdown':true, 'operator':true, 'invalid': errors[`Operator${index}`]}">
                                    <option disabled value="">Please select one</option>
                                    <option v-for="operator in getArithmeticOperator(index)" :key="operator.value" :value="operator.name">{{operator.value}}</option>
                                </Field>
                                <!-- End Numeric operator-->

                                <div v-if="isOperatorFilter(filter.name) && !isNullOperatorFilter(filter.name, filter.operator)">
                                    <Field :name="`Value${index}`" label="Filter value" :class="{'value':true, 'inputValue':true, 'invalid': errors[`Value${index}`]}" :rules="`${filter.arithmeticOperator !=''?'required':'required|between:'+[getMinValue(filter.name),getMaxValue(filter.name)]}`" type="number" v-model="filter.value" placeholder="Value" />
                                </div>
                                <div style="display:flex" v-if="isStringFilter(filter.name) && !isNullOperatorFilter(filter.name, filter.operator)">
                                    <p class="operatorLabel">=</p>
                                    <Field :name="`StringValue${index}`" label="Filter value" @input="setFieldValue('StringValue'+index, values['StringValue'+index].toUpperCase())" :class="{'value':true, 'inputValue':true, 'invalid': errors[`StringValue${index}`]}" :rules="`${'required|alpha_num' + '|length:'+ getStringLength(filter.name)}`" type="text" v-model="filter.stringValue" placeholder="Value" />
                                </div>
                                <div v-if="isBetweenOperatorFilter(filter.name, filter.operator) && !isNullOperatorFilter(filter.name, filter.operator)" style="display:flex">
                                    <p class="operatorLabel">AND</p>
                                    <Field :name="`Value2-${index}`" label="Filter value" :class="{'value':true, 'inputValue':true, 'invalid': errors[`Value2-${x}`]}" :rules="`${filter.arithmeticOperator !=''?'required':'required|between:'+[getMinValue(filter.name),getMaxValue(filter.name)]+'|min_value:@Value'+ index}`" type="number" v-model="filter.value2" placeholder="Value" />
                                </div>
                                <Field v-if="isMultiSelectFilter(filter.name) && !isNullOperatorFilter(filter.name, filter.operator)" :name="`MultiSelect${index}`" label="Filter value" as="select" :rules="`${'required'}`" v-model="filter.option" :class="{'multiselect': true, 'dropdown':true, 'invalid': errors[`MultiSelect${index}`]}" v-slot="{ value }" size="5" multiple>
                                    <option disabled value="">Please select</option>
                                    <option v-for="option in getOptionList(filter.name)" :key="option.displayName" :value="option.value" :selected="value.includes(option.displayName)">{{option.displayName}}</option>
                                </Field>
                                <p class="booleanLabel" v-if="isBooleanFilter(filter.name) && !isNullOperatorFilter(filter.name, filter.operator)">{{getLabel(filter.name)}}</p>
                                <p class="isNullLabel" v-if="isNullOperatorFilter(filter.name, filter.operator)">{{getOperator(filter.operator)}}</p>
                                <button :id="`addOperatorButton${index}`" class="logicalOperatorButton primaryButton" style="width: 40px;" @click="toggleLogicalOperatorDropdown(index)" type="button">+</button>
                                <Field :id="`LogicalOperatorDropdown${index}`" :name="`LogicalOperator${index}`" label="Logical Operator" as="select" v-model="filter.logicalOperator" :class="{'dropdown':true, 'operator':true}" @change="indentOnDropdownToggle(index)" hidden>
                                    <option value="">No Operator</option>
                                    <option v-for="operator in logicalOperators" :key="operator.value" :value="operator.value">{{operator.name}}</option>
                                </Field>
                                <button style="height: 2.5rem;" type="button" class="basicRemoveButton" v-on:click="removeFilter(index); validate(); indentOnFilterRemove(index,filter.logicalOperator)" v-if="filterDeleteButtonHide">
                                    <svg fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g clip-rule="evenodd" fill="rgb(0,0,0)" fill-rule="evenodd"><path d="m19.2803 4.71967c.2929.29289.2929.76777 0 1.06066l-13.49997 13.49997c-.29289.2929-.76777.2929-1.06066 0s-.29289-.7677 0-1.0606l13.50003-13.50003c.2929-.29289.7677-.29289 1.0606 0z" fill="rgb(0,0,0)" style="fill: rgb(255, 255, 255);"></path><path d="m4.71967 4.71967c.29289-.29289.76777-.29289 1.06066 0l13.49997 13.50003c.2929.2929.2929.7677 0 1.0606s-.7677.2929-1.0606 0l-13.50003-13.49997c-.29289-.29289-.29289-.76777 0-1.06066z" fill="rgb(0,0,0)" style="fill: rgb(255, 255, 255);"></path></g></svg>
                                </button>
                                <TooltipMessage v-if="isMultiSelectFilter(filter.name) && isLastMultiSelectFilter(index)">This is a multi-select filter, it enables you to select several items from the list. When multiple items are selected, the filter will apply to any item in the list. (e.g. If you select EmploymentStatus = Unemployed,Retired; then the simulator will eliminate anyone who is either Unemployed OR Retired)</TooltipMessage>
                            </div>
                            <ul class="errorList">
                                <template v-if="Object.keys(errors).length">
                                    <li v-for="(message, field) in errors" :key="field" class="invalidText">
                                        <span v-if="field.slice(-1) == index">{{ message }}</span>
                                    </li>
                                </template>
                            </ul>
                        </div>
                        <button v-on:click="addFilter(); validate(); indentOnFilterAdding()" class="primaryButton" type="button" v-if="selectedVertical && filters.length < 7">Add filter</button>
                        <p v-if="filters.length >= 7">Maximum filter limit reached</p>
                        <button style="margin-left:0.5rem" class="primaryButton" type="submit" :disabled="!meta.valid && !meta.pending" @click="lastLogicalOperatorCheck()">Submit</button>
                        <button style="margin-left:0.5rem" class="primaryButton" type="submit" :disabled="!meta.valid && !meta.pending" @click="getNowAndOpenSaveModal()">Save Filters</button>                        
                    </template>
                    <router-link class="button button-primary" to="/simulator/filters">Manage Filters</router-link>
                    <br />
                </Form>

                <div class="loader" id="loadingIcon" style="display:none;"></div>
            </div>
            <div class="column">
                <div id="chart"></div>
            </div>
        </div>

        <footer id="footer">
            <h2 id="disclaimerTitle">How Simulator Works</h2>
            <p>The Simulator tool returns results based on a random sample of searches&hellip; <a href @click.prevent="showMoreInfo = true">read more</a></p>
            <PopupMessage :enabled="showMoreInfo">
                <template v-slot:content>
                    <h2>How Simulator Works</h2>
                    <p>
                        The Simulator tool returns results based on a random sample of searches taken from a selection of our Price Comparison Websites representing 10% of their traffic. This sample is refreshed on a weekly basis to maintain an accurate snapshot of the market. The volumes displayed cover a period of a week and are extrapolated using the percentage of the sample hit by a given filter. They should therefore be considered an estimate. The chart shows percentages and weekly volumes of searches filtered out when each filter is applied in isolation, with the final bar representing the overall pass rate i.e. the percentage or volume of searches not hitting any of your filters.
                        <br />In order to display the projections, a data sample has been used to model, and rounding applied to figures to simplify the view.
                    </p>
                </template>
                <template v-slot:controls>
                    <button class="button button-primary" @click.prevent="showMoreInfo = false">Close</button>
                </template>
            </PopupMessage>
        </footer>       

        <popup-message :enabled="showSaveFilterMessage">
            <template v-slot:content>
                <h2>Save your filters</h2>
                <p>Save the filters you have created for use another time</p>
                <p v-if="filterID > 0" style="margin: 0 0 0.5rem 0;">Save new version:<input class="checkbox" type="checkbox" @click="enableDisableName()" id="chkSaveUpdateFilter" checked/></p>
                <p style="display:inline" inline>Filter Name: </p>
                <Field id="savedFilterName" name="savedFilterName" :class="{'saveName':true}" :rules="`${'required|alpha_num' + '|length: 3'}`" label="Saved filter name" type="text" v-model="saveFilterName" />
                <p>Timestamp: {{timeStamp}}</p>
                <p style="color:red;">*Filter name must be unique.</p>

                <p class="invalidText">{{ saveFilterError }}</p>
                <button style="margin-left:0.5rem" class="primaryButton" type="submit" @click="saveFilter()" id="btnSave">Save</button>                
                <button style="margin-left:0.5rem" class="primaryButton" type="submit" @click="closeSaveModal()">Close</button>
                <br />
                <p id="message" style="margin-top: 1.5rem; color: red;"></p>
            </template>
    </popup-message>


    </div>

</template>
<script>
    /* global newrelic */
    import { mapGetters, mapActions, mapMutations } from 'vuex'
    import { Field, Form, defineRule, configure } from 'vee-validate'
    import ApexCharts from 'apexcharts'
    import PopupMessage from '../PopupMessage.vue'
    import TooltipMessage from '../TooltipMessage.vue'
    import { FilterConfiguration, Filter } from '../../vuex/models'
    import { alpha_num, length, required } from '@vee-validate/rules'
    import { localize } from '@vee-validate/i18n'
    import vSelect from "vue-select";


    defineRule('length', length);
    defineRule('alpha_num', alpha_num);
    defineRule('required', required);

    defineRule('min_value', (value, [minValue]) => {
        if (value <= minValue) {
            return `Second value must be bigger than the first value`;
        }
        return true;
    });

    defineRule('between', (value, [minValue, maxValue]) => {
        if (value >= minValue && value <= maxValue) {
            return true
        }
        return "Filter value must be between " + Number(minValue).toLocaleString() + ' and ' + Number(maxValue).toLocaleString();
    });

    configure({
        validateOnInput: true,
        generateMessage: localize('en', {
            messages: {
                alpha_num: '{field} may only contain alphabetic characters or numbers',
                required: '{field} is required',
                length: '{field} length must be no more than {0} character(s) or number(s)'
            },
        }),
    })

    var chart;
    var roundOff = 500;
    var percentRoundOff = 0.1;

    export default {
        name: 'SimulatorView',
        components: {
            PopupMessage,
            Field,
            Form,
            TooltipMessage,
            vSelect
        },
        props: {
            filterID: {
                type: String
            },
            filter: {
                type: FilterConfiguration
            }
        },
        watch: {
            filterDeleteButtonHide: {
                deep: true,
                handler: function (value) {
                    this.filterDeleteButtonHide = value
                }
            },
            'user': 'checkAccess'
        },
        data() {
            return {
                ready: false,
                operators: [{ value: ">", name: "Greater" }, { value: ">=", name: "GreaterOrEqual" },
                { value: "=", name: "Equal" }, { value: "<=", name: "LessOrEqual" }, { value: "<", name: "Less" },
                { value: "BETWEEN", name: "Between" }, { value: "IS NULL", name: "IsNull" }, { value: "IS NOT NULL", name: "IsNotNull" }],
                verticals: [{ value: "Loans" }, { value: "Cards" }],
                logicalOperators: [{ value: "And", name: "AND" }, { value: "Or", name: "OR" }],
                arithmeticOperators: [{ value: "+", name: "Add" }, { value: "-", name: "Subtract" }, { value: "*", name: "Multiply" }, { value: "/", name: "Divide" }],
                isSubmitDisabled: false,
                isFilterAddedFlag: true,
                isLoading: false,
                selectedAggregators: [],
                filters: [new Filter()],
                disableRefresh: false,
                showSaveFilterMessage: false,
                saveFilterName: '',
                timeStamp: '',
                saveFilterError: '',
                loadFilterError: '',
                filterNames: [],
                filterNames2: [],
                selectedVertical: null,
                showMoreInfo: false,
                strAggregator: '',
                isArithmetic: true,
                previousFilterSetName: '',
                options: {
                    chart: {
                        type: 'bar',
                        redrawOnWindowResize: true,
                        height: '300px'
                    },
                    plotOptions: {
                        bar: {
                            horizontal: true
                        }
                    },
                    dataLabels: {
                        position: 'top',
                        offsetX: 50,
                        style: {
                            colors: ['#000000']
                        },
                        formatter: function (val, opt) {
                            if (opt.w.globals.series[0].includes(val) && opt.dataPointIndex < opt.w.globals.labels.length - 1 && opt.seriesIndex == 0 && (Number(val) >= percentRoundOff || Number(val) == 0)) return opt.w.globals.seriesNames[0] + ": " + Math.round(val * 10) / 10 + "%";
                            if (opt.w.globals.series[0].includes(val) && opt.dataPointIndex < opt.w.globals.labels.length - 1 && opt.seriesIndex == 0 && Number(val) < percentRoundOff) return opt.w.globals.seriesNames[0] + ": <" + percentRoundOff + "%";
                            if (opt.w.globals.series[0].includes(val) && opt.seriesIndex == 0 && (Number(val) >= percentRoundOff || Number(val) == 0)) return "Passed: " + Math.round(val * 10) / 10 + "%";
                            if (opt.w.globals.series[0].includes(val) && opt.seriesIndex == 0 && Number(val) < percentRoundOff) return "Passed: <" + percentRoundOff + "%";
                            if (opt.w.globals.series[1].includes(val) && opt.dataPointIndex == opt.w.globals.labels.length - 1 && opt.seriesIndex == 1) return "Passed Volume: " + Number(val).toLocaleString();
                            if (Number(val) < roundOff && Number(val) > 0) return opt.w.globals.seriesNames[1] + ": <" + Number(roundOff).toLocaleString();
                            return opt.w.globals.seriesNames[1] + ": " + Number(val).toLocaleString();
                        },
                    },
                    series: [{
                        name: 'Filtered out',
                        data: [],
                        color: function ({ dataPointIndex, w }) {
                            if (dataPointIndex == w.globals.labels.length - 1) return '#0C64AC';
                            return '#D03188';
                        }
                    },
                    {
                        name: 'Volume',
                        data: [],
                        color: function ({ dataPointIndex, w }) {
                            if (dataPointIndex == w.globals.labels.length - 1) return '#0C64AC';
                            return '#775699';
                        }
                    }],
                    title: {
                        text: 'Percentage of results filtered',
                        align: 'left',
                        margin: 10,
                        offsetX: 0,
                        offsetY: 0,
                        floating: false,
                        style: {
                            fontSize: '14px',
                            fontWeight: 'bold',
                            fontFamily: undefined,
                            color: '#263238'
                        },
                    },
                    subtitle: {
                        text: ['', '']
                    },
                    xaxis: {
                        categories: [],
                        labels: {
                            formatter(val) {
                                return Number(val).toLocaleString()
                            }
                        }
                    }
                }
            }
        },
        async created() {
            this.checkAccess()
            let oktaToken = await this.$auth.getAccessToken();
            await this.getFilterList(oktaToken);
            await this.getAggregatorList(oktaToken);
            this.createChart();
            this.checkAggregatorList();
            await this.loadPreviousFilter();
        },
        computed: {
            ...mapGetters(['loadedViz', 'views', 'summaryView', 'selectedContent', 'filterList', 'aggregatorList', 'user']),
            filterDeleteButtonHide() {
                if (this.filters.length == 1) {
                    return false;
                }
                else {
                    return true;
                }
            }
        },
        methods: {
            ...mapActions(['login', 'submitSimulatorParams', 'getFilterList', 'getAggregatorList', 'submitSaveFilterParams', 'getPreviousFilterParams', 'submitUpdateFilterParams']),
            ...mapMutations(['SELECT_CONTENT']),
            createChart() {
                chart = new ApexCharts(document.querySelector("#chart"), this.options);

                chart.render();
            },
            async loadPreviousFilter() {
                let oktaToken = await this.$auth.getAccessToken();

                if (this.filterID != null) {
                    this.disableRefresh = true
                    try {
                        var previousFilters = await this.getPreviousFilterParams({ payload: this.filterID, oktaToken });
                        this.previousFilterSetName = previousFilters.name;
                        var filterData = JSON.parse(previousFilters.data)
                        this.selectedVertical = filterData.vertical
                        this.selectedAggregators = filterData.aggregators

                        var isArithmetic = false;

                        var filterNumber = 0

                        for (var y = 0; y < filterData.filters.length; y++) {
                            if (!isArithmetic) {
                                const name = this.filterList.find((element) => element.name.split('-')[0] == filterData.filters[y].name);

                                this.filters[filterNumber].name = name.name;
                                this.filters[filterNumber].operator = filterData.filters[y].operator
                                this.filters[filterNumber].value = filterData.filters[y].value
                                this.filters[filterNumber].option = filterData.filters[y].option
                                this.filters[filterNumber].logicalOperator = filterData.filters[y].logicalOperator
                                this.filters[filterNumber].value2 = filterData.filters[y].value2
                                this.filters[filterNumber].stringValue = filterData.filters[y].stringValue


                                if (filterData.filters[y].arithmeticOperator != null && filterData.filters[y].arithmeticOperator.length !== 0) {
                                    this.filters[filterNumber].arithmeticOperator = filterData.filters[y].arithmeticOperator
                                    const name2 = this.filterList.find((element) => (element.name.replace("-" + element.name.split("-").pop(), "")) == filterData.filters[y + 1].name);
                                    if (name2 != null) { this.filters[filterNumber].name2 = name2.name }
                                    isArithmetic = true
                                }
                                else {
                                    filterNumber++;
                                    if (y < filterData.filters.length - 1) {
                                        this.filters.push(new Filter());
                                    }
                                }

                                this.getVerticalFilterList()
                                if (isArithmetic) this.getArithmeticFilterList(filterData.filters[y].arithmeticOperator);
                            }
                            else {
                                isArithmetic = false
                                if (filterData.filters[y].logicalOperator != null && filterData.filters[y].logicalOperator.length !== 0) {
                                    this.filters[filterNumber].logicalOperator = filterData.filters[y].logicalOperator
                                }
                                filterNumber++;
                                if (y < filterData.filters.length - 1) {
                                    this.filters.push(new Filter());
                                }
                            }
                        }

                        await this.Sleep(2000)

                        for (var x = 0; x < this.filters.length; x++) {
                            if (this.filters[x].logicalOperator != null && this.filters[x].logicalOperator.length !== 0) {
                                this.toggleLogicalOperatorDropdown(x)
                                this.indentOnDropdownToggle(x)
                            }
                        }
                    }
                    catch (error) {
                        this.loadFilterError = "Could not load filter set for ID " + this.filterID + "."
                    }
                }
            },
            TogglePopup() {
                this.buttonTrigger = !this.buttonTrigger
            },
            async Sleep(ms) {
                return new Promise(resolve => setTimeout(resolve, ms));
            },
            refreshChart() {
                chart.updateOptions(this.options);
            },
            addFilter() {
                this.isFilterAddedFlag = true;
                this.filters.push(new Filter());
            },
            removeFilter(filterIndex) {
                if (this.filters.length > 1) {
                    this.filters.splice(filterIndex, 1);
                }
            },
            closeSaveModal() {
                this.showSaveFilterMessage = false;
            },

            enableDisableName() {
                var chk = document.getElementById("chkSaveUpdateFilter");
                var field = document.getElementById("savedFilterName");
                var btnSave = document.getElementById("btnSave");
                if (chk.checked) {
                    this.saveFilterName = "Filter Set " + this.timeStamp;
                    field.disabled = false;
                    btnSave.textContent = "Save";
                }
                else {
                    this.saveFilterName = this.previousFilterSetName;
                    field.disabled = true;
                    btnSave.textContent = "Update";
                }
            },
            getNowAndOpenSaveModal() {
                var d = new Date();
                var datestring = ("0" + d.getDate()).slice(-2) + "-" + ("0" + (d.getMonth() + 1)).slice(-2) + "-" +
                    d.getFullYear() + " " + ("0" + d.getHours()).slice(-2) + ":" + ("0" + d.getMinutes()).slice(-2) + ":" + ("0" + d.getSeconds()).slice(-2);
                this.saveFilterName = "Filter Set " + datestring;
                this.timeStamp = datestring;
                this.showSaveFilterMessage = true;
            },
            async saveFilter() {
                this.saveFilterError = '';
                if (this.saveFilterName.length < 3) {
                    this.saveFilterError += '\nFilter name must be at least 3 characters in length';
                }
                else if (!this.saveFilterName.match(/^[A-Za-z0-9_ :-]+$/)) {
                    this.saveFilterError += '\nFilter name must only contain alphanumeric characters, or _, -, :, and spaces';
                }

                if (this.saveFilterError == '') {


                    let submissionData = {
                        vertical: this.selectedVertical,
                        aggregators: this.selectedAggregators,
                        filters: []
                    };

                    let oktaToken = await this.$auth.getAccessToken();

                    for (let x = 0; x < this.filters.length; x++) {
                        if (this.isBooleanFilter(this.filters[x].name)) {
                            this.filters[x].option = "true";
                        }

                        if (this.filters[x].option == null || this.filters[x].option === 'undefined'
                            || this.filters[x].option == '') {
                            this.filters[x].option = "None";
                        }

                        let filterType = this.filters[x].name.split("-").pop();

                        submissionData.filters.push({
                            name: this.filters[x].name.replace("-" + filterType, ""),
                            operator: this.filters[x].operator == "" ? null : this.filters[x].operator,
                            logicalOperator: this.filters[x].logicalOperator == "" ? null : this.filters[x].logicalOperator,
                            value: filterType == "Operator" ? parseInt(this.filters[x].value) : null,
                            value2: this.filters[x].operator == "Between" ? parseInt(this.filters[x].value2) : null,
                            option: this.filters[x].option.toString(),
                            stringValue: filterType == "String" ? this.filters[x].stringValue : null,
                            arithmeticOperator: this.filters[x].arithmeticOperator == "" ? null : this.filters[x].arithmeticOperator,
                        });

                        if (this.filters[x].arithmeticOperator != "") {
                            submissionData.filters.push({
                                name: this.filters[x].name2.replace("-" + filterType, ""),
                                operator: this.filters[x].operator == "" ? null : this.filters[x].operator,
                                logicalOperator: this.filters[x].logicalOperator == "" ? null : this.filters[x].logicalOperator,
                                value: filterType == "Operator" ? parseInt(this.filters[x].value) : null,
                                value2: this.filters[x].operator == "Between" ? parseInt(this.filters[x].value2) : null,
                                option: this.filters[x].option.toString(),
                                stringValue: filterType == "String" ? this.filters[x].stringValue : null
                            });

                            submissionData.filters[submissionData.filters.length - 2].logicalOperator = null;
                        }
                    }
                    var filterData = JSON.stringify(submissionData);

                    let filterSet = {
                        data: filterData,
                        ID: (this.filterID != null ? parseInt(this.filterID) : 0),
                        name: this.saveFilterName,
                        userName: 'TestUser',
                        companyName: 'TestCompany',
                    }

                    var chk = document.getElementById("chkSaveUpdateFilter");
                    if (this.filterID > 0 && !chk.checked) {
                        var strResponse = await this.submitUpdateFilterParams({ payload: filterSet, oktaToken });
                    }
                    else {
                        strResponse = await this.submitSaveFilterParams({ payload: filterSet, oktaToken });
                    }
                    if (strResponse) document.getElementById('message').textContent = strResponse.data.content;
                    else document.getElementById('message').textContent = "Failed to save filter set";

                }
            },
            async onSubmit() {

                this.isFilterAddedFlag = false;
                if (this.isBlankValueInFilters || this.isLoading) {
                    this.isSubmitDisabled = true;
                    this.isDisabled;
                    return;
                }

                let submissionData = {
                    vertical: this.selectedVertical,
                    aggregators: this.selectedAggregators,
                    filters: []
                };

                let oktaToken = await this.$auth.getAccessToken();

                for (let x = 0; x < this.filters.length; x++) {
                    if (this.isBooleanFilter(this.filters[x].name)) {
                        this.filters[x].option = "true";
                    }

                    if (this.filters[x].option == null || this.filters[x].option === 'undefined'
                        || this.filters[x].option == '') {
                        this.filters[x].option = "None";
                    }

                    let filterType = this.filters[x].name.split("-").pop();

                    submissionData.filters.push({
                        name: this.filters[x].name.replace("-" + filterType, ""),
                        operator: this.filters[x].operator == "" ? null : this.filters[x].operator,
                        logicalOperator: this.filters[x].logicalOperator == "" ? null : this.filters[x].logicalOperator,
                        value: filterType == "Operator" ? parseInt(this.filters[x].value) : null,
                        value2: this.filters[x].operator == "Between" ? parseInt(this.filters[x].value2) : null,
                        option: this.filters[x].option.toString(),
                        stringValue: filterType == "String" ? this.filters[x].stringValue : null,
                        arithmeticOperator: this.filters[x].arithmeticOperator == "" ? null : this.filters[x].arithmeticOperator,
                    });

                    if (this.filters[x].arithmeticOperator != "") {
                        submissionData.filters.push({
                            name: this.filters[x].name2.replace("-" + filterType, ""),
                            operator: this.filters[x].operator == "" ? null : this.filters[x].operator,
                            logicalOperator: this.filters[x].logicalOperator == "" ? null : this.filters[x].logicalOperator,
                            value: filterType == "Operator" ? parseInt(this.filters[x].value) : null,
                            value2: this.filters[x].operator == "Between" ? parseInt(this.filters[x].value2) : null,
                            option: this.filters[x].option.toString(),
                            stringValue: filterType == "String" ? this.filters[x].stringValue : null
                        });

                        submissionData.filters[submissionData.filters.length - 2].logicalOperator = null;
                    }
                }

                if (typeof newrelic !== 'undefined' && newrelic.interaction) {
                    try {
                        const interaction = newrelic.interaction();
                        if (interaction && typeof interaction.setAttribute === 'function') {
                            interaction.setAttribute('simulatorVertical', this.selectedVertical)
                                .setAttribute('simulatorFilterCount', submissionData.filters.length)
                                .save()
                                .end();
                        }
                    } catch (error) {
                        console.warn('New Relic interaction tracking failed:', error);
                    }
                }

                if (typeof newrelic !== 'undefined' && newrelic.interaction) {
                    submissionData.aggregators.forEach(agg => {
                        try {
                            const interaction = newrelic.interaction();
                            if (interaction && typeof interaction.setAttribute === 'function') {
                                interaction.setAttribute('simulatorAggregator', agg).save().end();
                            }
                        } catch (error) {
                            console.warn('New Relic aggregator tracking failed:', error);
                        }
                    });
                }

                if (typeof newrelic !== 'undefined' && newrelic.interaction) {
                    submissionData.filters.forEach(filter => {
                        try {
                            const interaction = newrelic.interaction();
                            if (interaction && typeof interaction.setAttribute === 'function') {
                                interaction.setAttribute('simulatorFilter', filter.name).save().end();
                            }
                        } catch (error) {
                            console.warn('New Relic filter tracking failed:', error);
                        }
                    });
                }

                document.getElementById('loadingIcon').style.display = "inline-block";
                this.isLoading = true;

                let data = await this.submitSimulatorParams({ payload: submissionData, oktaToken })

                setTimeout(() => {
                    document.getElementById('loadingIcon').style.display = "none";
                    this.isLoading = false;
                }, 100);

                this.clearTableValues();
                if (data) { this.loadTableValues(data); } else { console.log('Return dataset is empty.'); }
                this.refreshChart();
            },
            loadTableValues(data) {
                data.forEach(value => {
                    this.options.series[0].data.push(value.percentage);
                    if (value.volume > 249) this.options.series[1].data.push(this.roundToNearest(value.volume));
                    else this.options.series[1].data.push(value.volume);

                    if (value.rule != null) this.options.xaxis.categories.push(value.rule);
                    else this.options.xaxis.categories.push(this.toTitleCase(value.name));
                });

                for (var x = 0; x < this.selectedAggregators.length; x++) {
                    if (x == this.selectedAggregators.length - 1 && this.selectedAggregators.length != 1) {
                        this.strAggregator = this.strAggregator.substr(0, this.strAggregator.length - 2);
                        this.strAggregator += " and " + this.selectedAggregators[x] + ")";
                    }
                    else {
                        this.strAggregator += this.selectedAggregators[x]
                        if (this.selectedAggregators.length != 1) this.strAggregator += ", ";
                    }
                }
                this.options.subtitle.text[0, 0] = "Distinct results of each individual filter, when applied to a sample of data from the selected PCWs (";
                if (this.selectedAggregators.length == 1) this.options.subtitle.text[0, 1] = this.strAggregator.substr(0, this.strAggregator.length) + ")";
                else if (this.aggregatorList.length == 1) this.options.subtitle.text[0, 1] = this.aggregatorList[0].aggregatorName + ")";
                else this.options.subtitle.text[0, 1] = this.strAggregator.substr(0, this.strAggregator.length);
                this.options.chart.height = 'auto';
            },
            clearTableValues() {
                this.options.series[0].data = [];
                this.options.series[1].data = [];
                this.options.xaxis.categories = [];
                this.strAggregator = '';
            },
            toTitleCase(str) {
                str = str.replace(/([_])/g, ' ');
                str = str.replace(/([A-Z])/g, ' $1').trim();

                return str.replace(
                    /\b\w+('\w{1})?/g,
                    function (txt) {
                        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                    }
                )
            },
            getVerticalFilterList() {
                var resultFilters = this.filterList.filter((filter) => filter.vertical == null || filter.vertical == this.selectedVertical);
                this.filterNames = resultFilters.map((filter) => ({ label: filter.displayName, name: filter.name }));
            },
            isOperatorFilter(filterName) {
                if (filterName !== null && filterName.split("-").pop() == "Operator") {
                    return true;
                }
                return false;
            },
            isStringFilter(filterName) {
                if (filterName !== null && filterName.split("-").pop() == "String") {
                    return true;
                }
                return false;
            },
            isBetweenOperatorFilter(filterName, operatorSelected) {
                if (filterName !== null && filterName.split("-").pop() == "Operator") {
                    if (operatorSelected != undefined && operatorSelected == "Between")
                        return true;
                }
                return false;
            },
            isMultiSelectFilter(filterName) {
                if (filterName !== null && filterName.split("-").pop() == "MultiSelect") {
                    return true;
                }
                return false;
            },
            isBooleanFilter(filterName) {
                if (filterName !== null && filterName.split("-").pop() == "Boolean") {
                    return true;
                }
                return false;
            },
            getLabel(filterName) {
                if (filterName !== null) {
                    return this.filterList.find(filter => filter.name === filterName).label;
                }
                return "No Label";
            },
            getOptionList(filterName) {
                for (var x = 0; x < this.filterList.length; x++) {
                    if (this.filterList[x].name == filterName && this.filterList[x].options != null) {
                        return this.filterList[x].options.filter(options => { return options.vertical == null || options.vertical == this.selectedVertical });
                    }
                }
                return false;
            },
            filterAggregatorList(aggList) {
                return aggList.filter(aggList => aggList.vertical == this.selectedVertical || aggList.vertical == null);
            },
            getMinValue(filterName) {
                for (var x = 0; x < this.filterList.length; x++) {
                    if (this.filterList[x].name == filterName) {
                        return this.filterList[x].minValue;
                    }
                }
                return null;
            },
            getMaxValue(filterName) {
                for (var x = 0; x < this.filterList.length; x++) {
                    if (this.filterList[x].name == filterName) {
                        return this.filterList[x].maxValue;
                    }
                }
                return null;
            },
            getStringLength(filterName) {
                for (var x = 0; x < this.filterList.length; x++) {
                    if (this.filterList[x].name == filterName) {
                        return this.filterList[x].length;
                    }
                }
                return null;
            },
            resetFilter(filterIndex) {
                this.filters[filterIndex].option = '';
                this.filters[filterIndex].operator = '';
                this.filters[filterIndex].logicalOperator = '';
                this.filters[filterIndex].value = '';
                this.filters[filterIndex].value2 = '';
                this.filters[filterIndex].stringValue = '';
                this.filters[filterIndex].arithmeticOperator = '';
                this.filters[filterIndex].name2 = '';
            },
            refreshForm() {
                if (!this.disableRefresh) {
                    this.selectedAggregators = [];
                    this.filters = [new Filter()];
                }
            },
            async checkAccess() {
                if (this.user && (!this.user.company.hasFeature('Simulator') && !this.user.internalUser)) {
                    this.$router.push('/performance')
                }
                else {
                    this.ready = true
                }
            },
            roundToNearest(number) {
                return Math.round(number / roundOff) * roundOff;
            },
            checkAggregatorList() {
                if (this.aggregatorList == null) {
                    console.error('No values are available in aggregator list.');
                }
                else if (this.aggregatorList.length == 1) {
                    this.selectedAggregators = this.aggregatorList.aggregatorName;
                }
            },

            isNullOperatorFilter(filterName, operatorSelected) {
                if (filterName !== null && filterName.split("-").pop() == "Operator") {
                    if (operatorSelected != undefined && (operatorSelected == "IsNull" || operatorSelected == "IsNotNull"))
                        return true;
                }
                return false;
            },
            getOperator(operatorName) {
                if (operatorName !== undefined) {
                    return this.operators.find(operator => operator.name === operatorName).value;
                }
                return "No Label";
            },
            isLastMultiSelectFilter(index) {
                let lastMultiselectFilter = null

                for (let i = 0; i < this.filters.length; i++) {
                    if (this.isMultiSelectFilter(this.filters[i].name)) {
                        lastMultiselectFilter = i
                    }
                }

                return lastMultiselectFilter == index
            },
            toggleLogicalOperatorDropdown(currentIndex) {
                let dropdownName = "LogicalOperatorDropdown" + currentIndex;
                let targetDropdown = document.getElementById(dropdownName);
                let isDropdownHidden = targetDropdown.hidden;

                if (isDropdownHidden) {
                    targetDropdown.hidden = false;
                    document.getElementById("addOperatorButton" + currentIndex).textContent = "-";
                } else {
                    targetDropdown.hidden = true;
                    document.getElementById("addOperatorButton" + currentIndex).textContent = "+";
                    this.filters[currentIndex].logicalOperator = '';
                    this.indentOnDropdownToggle(currentIndex);
                }
            },
            indentOnFilterAdding() {
                let currentIndex = this.filters.length - 1;

                if (currentIndex != 0) {
                    let precedingLogicalOperator = this.filters[currentIndex - 1].logicalOperator;

                    if (precedingLogicalOperator) {
                        let dropdownName = "filter" + currentIndex;

                        setTimeout(function () {
                            document.getElementById(dropdownName).style.marginLeft = '2rem';
                        }, 10);
                    }
                }
            },
            indentOnDropdownToggle(currentIndex) {
                let nextFilterIndex = currentIndex + 1;
                let isNextFilterExist = typeof this.filters[nextFilterIndex] !== 'undefined';
                let isCurrentLogicalOperatorExist = this.filters[currentIndex].logicalOperator == '';
                let dropdownName = "filter" + nextFilterIndex;

                if (isNextFilterExist && isCurrentLogicalOperatorExist) {
                    document.getElementById(dropdownName).style.marginLeft = '0';
                }

                if (isNextFilterExist && !isCurrentLogicalOperatorExist) {
                    document.getElementById(dropdownName).style.marginLeft = '2rem';
                }
            },
            indentOnFilterRemove(currentIndex, deletedLogicalOperator) {
                if (deletedLogicalOperator === '') {
                    let isPrecedingFilterExist = typeof this.filters[currentIndex - 1] !== 'undefined';

                    if (isPrecedingFilterExist) {
                        let precedingFilterDropdownName = "filter" + (currentIndex - 1);
                        let precedingFilter = document.getElementById(precedingFilterDropdownName);
                        let isPrecedingFilterIndented = precedingFilter.style != null && precedingFilter.style != "" ? true : false;
                        let isPrecedingLogicalOperator = this.filters[currentIndex - 1].logicalOperator !== '';

                        if (isPrecedingFilterIndented && isPrecedingLogicalOperator) {
                            this.filters[currentIndex - 1].logicalOperator = '';
                        }
                    }
                }

                for (let index = 0; index < this.filters.length; index++) {
                    let isNextFilterExist = typeof this.filters[index] !== 'undefined';
                    let isPrecedingFilterExist = typeof this.filters[index - 1] !== 'undefined';

                    if (isNextFilterExist && isPrecedingFilterExist) {
                        let precedingFilterDropdownName = "filter" + (index - 1);
                        let precedingFilter = document.getElementById(precedingFilterDropdownName);
                        let isPrecedingFilterIndented = precedingFilter.style != null && precedingFilter.style != "" ? true : false;
                        let isPrecedingLogicalOperator = this.filters[index - 1].logicalOperator !== '';
                        let currentFilterDropdownName = "filter" + index;

                        if (isPrecedingLogicalOperator && isPrecedingFilterIndented) {
                            document.getElementById(currentFilterDropdownName).style.marginLeft = '2rem';
                        } else {
                            document.getElementById(currentFilterDropdownName).style.marginLeft = '0';
                        }
                    }

                    if (isNextFilterExist && !isPrecedingFilterExist) {
                        let currentFilterDropdownName = "filter" + index;
                        document.getElementById(currentFilterDropdownName).style.marginLeft = '0';
                    }
                }
            },
            lastLogicalOperatorCheck() {
                if (this.filters[this.filters.length - 1].logicalOperator !== '') {
                    this.filters[this.filters.length - 1].logicalOperator = '';
                }
            },
            isArithmeticFilter(filterName, operatorSelected) {
                if (filterName !== null && filterName.split("-").pop() == "Operator") {
                    if (operatorSelected != undefined && (operatorSelected != "IsNull" || operatorSelected != "IsNotNull"))
                        return true;
                }
                return false;
            },
            getArithmeticFilterList(operatorSelected) {
                if (operatorSelected != undefined && (operatorSelected != "No Operator") && (operatorSelected != "")) {
                    var resultFilters = this.filterList.filter((filter) => (filter.vertical == null || filter.vertical == this.selectedVertical) && filter.filterType == "Operator");
                    this.filterNames2 = resultFilters.map((filter) => ({ label: filter.displayName, name: filter.name }));
                }
            },
            getArithmeticOperator(index) {
                if (this.filters[index].arithmeticOperator) {
                    return this.operators.filter(operator => operator.name != "IsNull" && operator.name != "IsNotNull");
                }
                return this.operators;
            },
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../styles/base';
    @import '../../../node_modules/hd-styles/modules/mixins';
    @import 'vue-select/dist/vue-select.css';

    .inputValue {
        a {
            color: $blue-mid;

            &:hover, &:focus {
                color: $blue;
            }
        }

        border: $grey-dark $border-width solid;
        padding-left: 5px;
        border-radius: $border-radius-large;
        height: 40px;
    }

    .saveName {
        a {
            color: $blue-mid;

            &:hover, &:focus {
                color: $blue;
            }
        }

        border: grey 2px solid;
        padding-left: 5px;
        border-radius: 6px;
        height: 40px;
        width: 300px;
    }

    .invalid {
        --bg-opacity: 1;
        border: solid $red-error $border-width-light !important;
        box-shadow: 0 0 0 1px $red-error;
        background-color: $red-background;
        border-radius: $border-radius-large;
    }

    .invalidText {
        color: $red-error;
    }

    .multiselect {
        display: inline-block;
        align-self: flex-end;
        height: 100%;
        width: 100%;
    }

    .dropdown:hover {
        border-color: $blue-mid;
        color: $black;
    }

    .primaryButton {
        height: 40px;
        color: $white;
        border-radius: $border-radius-mid;
        background-color: $blue-mid;
        width: 45%;
        margin-top: 0;
        margin-left: 0.7rem;
        border: $border-width-light solid;
    }

    .primaryButton:hover {
        color: $white;
        border-radius: $border-radius-mid;
        background-color: $blue;
    }

    .primaryButton:disabled {
        color: #92939b;
        border-radius: $border-radius-mid;
        background-color: #f8f4f4;
        cursor: not-allowed;
    }

    .primaryButton:focus {
        box-shadow: 0 0 0 1px $blue-mid;
    }

    .basicRemoveButton {
        border-radius: $border-radius-mid;
        background-color: $red-600;
        border: $border-width-light solid $white;
        padding: 5px 8px 0px 8px;
        margin-top: 0;
        width: 40px;
        margin-left: 0;
        margin-top: 0.2rem;
    }

    .basicRemoveButton:hover {
        background-color: $red-800;
    }

    .logicalOperatorButton {
        margin-right: 0.5rem;
        width: 40px;
        font-size: $beta-font-size;
    }

    .filterInput {
        display: grid;
        white-space: nowrap;
        padding: 1px 1px 1px 1px;
    }

    .filterInput ::v-deep .vs__dropdown-toggle {
        height: 40px;
        border: $border-width solid $grey-dark;
        border-radius: $border-radius-large;
    }

    .filterInput ::v-deep .vs__dropdown-toggle:hover {
        border-color: $blue-mid;
    }

    .filterInput ::v-deep .vs__selected-options {
        overflow: hidden;
    }

    .filterInput ::v-deep .vs__selected {
        margin: 6px 2px 0px;
        white-space: pre;
    }

    .filterInput ::v-deep .vs__dropdown-menu {
        width: auto;
    }

    .filterInputContainer {
        margin-bottom: 1rem;
    }

    .booleanLabel {
        text-align: center;
        padding: 0.5rem;
    }

    .dropdown {
        margin-bottom: 0.2rem;
        margin-right: 0;
        width: 100%;
        border-color: $grey-dark;
        color: $black;
    }

    .searchDropdown {
        width: 12rem !important;
        margin-right: 0.5rem;
    }

    .top {
        display: grid;
        margin-bottom: 1rem;
    }

    select, .vertical, .inputValue {
        margin-right: 0;
    }

    .operator, .inputValue {
        width: 100%;
    }

    .select, .inputValue {
        width: 100%;
    }

    .errorList {
        @include list-reset;
    }

    .header {
        box-shadow: 0 0 5px $grey-dark;
        border-radius: $border-radius-large;
        text-align: center;
        padding: 1rem;

        p {
            margin: 0;
        }
    }

    #disclaimerTitle {
        text-align: center;
        margin: 0;
        font-weight: 500;
    }

    @include bp('small-medium') {
        select, input, .booleanLabel, isNullLabel {
            margin-right: 0.5rem;
        }

        .booleanLabel {
            margin-bottom: 0;
            width: 17rem;
        }

        .isNullLabel {
            margin-bottom: 0;
            width: 7rem;
            text-align: center;
            padding: 0.5rem;
        }

        .dropdown {
            width: 12rem;
            margin-bottom: 0;
            margin-right: 0.5rem;
        }

        .operator {
            width: 8rem;
        }

        .inputValue {
            width: 5.5rem;
        }

        .operatorLabel {
            margin-left: -0.5rem;
            margin-bottom: 0;
            padding: 0.5rem 0.5rem 0 0.5rem;
            font-size: $gamma-font-size;
        }

        .filterInput {
            display: flex;
            flex-wrap: wrap;
        }

        .top {
            margin-bottom: 2rem;
            display: flex;
        }

        .basicButton {
            width: 100px;
        }

        .basicRemoveButton {
            margin-top: 0;
            width: 40px;
        }

        .inputValue {
            margin-right: 0.5rem;
        }

        .primaryButton {
            margin-left: 0;
            margin-top: 0;
            width: 100px;
        }

        .multiselect {
            width: 17rem;
        }

        #verticalDropdown {
            width: 18.5rem;
        }

        #footer {
            bottom: 0;
            width: 100%;
            padding: 10px 15px 20px;
            margin-top: auto;
            flex-shrink: 0;

            > p {
                text-align: center;
            }
        }

        .checkbox {
            height: 25px;
            width: 25px;
            margin-left:10px;
        }
    }

    @include bp('medium-large') {
        .form-content {
            display: flex;
            justify-content: space-between;
        }

        .column {
            width: calc(50% - $spacing-unit / 2);
        }
    }

    .loader {
        border: 8px solid #f3f3f3;
        border-top: 8px solid #b44fe3;
        border-radius: 100%;
        width: 60px;
        height: 60px;
        animation: spin 2s linear infinite;
        margin-left: 40%;
        margin-top: -15%;
        position: absolute;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }
</style>